import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import GrindrodLogo from "../components/GrindrodLogo";
import {
    Play, Pause, Stop, MapPin, Gauge as GaugeIcon, Truck, Warning, Clock, ArrowsClockwise, SignOut,
    VideoCamera, ArrowsHorizontal as ArrowsHorizontalIcon,
} from "@phosphor-icons/react";
import OperatorMiniMap from "../components/OperatorMiniMap";

/* FEL Operator dashboard.
   - Mobile-friendly. Operator keeps the screen open in the cabin.
   - Geolocation watch every 2s → POST /api/fel/sessions/{id}/ping
   - Proximity warning (< 10m to another active operator): full-screen overlay + audible beep on BOTH devices.
   - Pause/stop with reasons. */

const PING_INTERVAL_MS = 2000;

export default function FELOperator() {
    const { user, logout } = useAuth();
    const [active, setActive] = useState(null); // active session for me
    const [loading, setLoading] = useState(true);
    const [showStart, setShowStart] = useState(false);
    const [showPause, setShowPause] = useState(false);
    const [showStop, setShowStop] = useState(false);
    const [showRefuel, setShowRefuel] = useState(false);
    const [proximity, setProximity] = useState([]); // alerts from the latest ping
    const [warnings, setWarnings] = useState([]);      // ≤30 m — soft warn
    const [nearby, setNearby] = useState([]);          // ≤100 m — visible on mini-map
    const [zones, setZones] = useState({ alarm_m: 10, warn_m: 30, visible_m: 100 });
    const [lastPos, setLastPos] = useState(null);
    const [currentSpeed, setCurrentSpeed] = useState(0);
    const [gpsState, setGpsState] = useState("idle"); // idle | acquiring | live | denied | error
    const watchIdRef = useRef(null);
    const lastPingRef = useRef(0);
    const audioCtxRef = useRef(null);
    const lastAlertAtRef = useRef(0);

    const loadActive = async () => {
        try {
            const r = await api.get("/fel/sessions/me");
            setActive(r.data || null);
        } catch (e) {
            // 404 = operator has no active shift session yet; anything else
            // is a soft failure (network hiccup) that will retry on next
            // interaction — surface for debugging without blocking the UI.
            if (e?.response?.status !== 404) {
                console.warn("FEL sessions/me failed:", e?.message || e);
            }
        }
        setLoading(false);
    };

    useEffect(() => { loadActive(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    // Load active machines for the start modal
    const [machines, setMachines] = useState([]);
    useEffect(() => {
        if (!showStart) return;
        api.get("/fel/machines", { params: { in_service: true } }).then((r) => setMachines(r.data || [])).catch(() => {});
    }, [showStart]);

    // Start GPS watch when there's an active session
    useEffect(() => {
        if (!active || active.status !== "active") {
            stopWatch();
            return;
        }
        startWatch();
        return stopWatch;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active?.id, active?.status]);

    const startWatch = () => {
        if (!navigator.geolocation) {
            setGpsState("denied");
            toast.error("Geolocation not supported on this device");
            return;
        }
        setGpsState("acquiring");
        watchIdRef.current = navigator.geolocation.watchPosition(
            (pos) => {
                setGpsState("live");
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                const speedMs = pos.coords.speed; // m/s, may be null
                const speedKmh = (speedMs != null && !isNaN(speedMs)) ? Math.max(0, speedMs * 3.6) : 0;
                setLastPos({ lat, lng, ts: Date.now() });
                setCurrentSpeed(speedKmh);
                const now = Date.now();
                if (now - lastPingRef.current >= PING_INTERVAL_MS && active?.id) {
                    lastPingRef.current = now;
                    sendPing(active.id, lat, lng, speedKmh, pos.coords.heading, pos.coords.accuracy);
                }
            },
            (err) => {
                console.error("GPS error", err);
                if (err.code === err.PERMISSION_DENIED) {
                    setGpsState("denied");
                    toast.error("Location permission denied — open Settings and allow Location for this site.");
                } else {
                    setGpsState("error");
                }
            },
            { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
        );
    };

    const stopWatch = () => {
        if (watchIdRef.current != null && navigator.geolocation) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
        setGpsState("idle");
    };

    /* -------------- LIVE VIEW (manager-triggered camera) -------------- */
    const liveViewActiveRef = useRef(false);
    const liveStreamRef = useRef(null);
    const liveVideoRef = useRef(null);
    const liveCanvasRef = useRef(null);
    const liveFrameTimerRef = useRef(null);
    const pushFrameBusyRef = useRef(false);
    const liveCheckTimerRef = useRef(null);
    const [liveViewActive, setLiveViewActive] = useState(false);

    const stopLiveView = () => {
        if (liveFrameTimerRef.current) { clearInterval(liveFrameTimerRef.current); liveFrameTimerRef.current = null; }
        if (liveStreamRef.current) {
            try { liveStreamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* */ }
            liveStreamRef.current = null;
        }
        liveViewActiveRef.current = false;
        setLiveViewActive(false);
    };

    const startLiveView = async (sessionId) => {
        if (liveViewActiveRef.current) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: "environment" }, width: { ideal: 480 }, height: { ideal: 360 }, frameRate: { ideal: 30 } },
                audio: false,
            });
            liveStreamRef.current = stream;
            if (liveVideoRef.current) {
                liveVideoRef.current.srcObject = stream;
                await liveVideoRef.current.play().catch(() => {});
            }
            liveViewActiveRef.current = true;
            setLiveViewActive(true);
            toast.success("Manager is now viewing your camera");
            // Push frames at ~30 fps with single-in-flight guard so we never
            // stack up POSTs if the network gets slow.
            liveFrameTimerRef.current = setInterval(() => pushFrame(sessionId), 33);
        } catch (e) {
            console.error("camera error", e);
            toast.error("Camera permission denied — open Settings and allow Camera for this site.");
            // tell backend to drop the request so it isn't perpetually pending
            try { await api.post(`/fel/sessions/${sessionId}/live-view/stop`); } catch { /* */ }
        }
    };

    const pushFrame = async (sessionId) => {
        // Single in-flight guard — if last upload still pending, drop this frame
        if (pushFrameBusyRef.current) return;
        pushFrameBusyRef.current = true;
        try {
            const v = liveVideoRef.current;
            const c = liveCanvasRef.current;
            if (!v || !c || v.videoWidth === 0) return;
            // 320x240 capture — keeps payload ~12-18 KB per frame at q=0.5,
            // sustainable at 30 fps over typical mobile uplink.
            const W = 320;
            c.width = W;
            c.height = Math.round(W * (v.videoHeight / v.videoWidth));
            const ctx = c.getContext("2d");
            ctx.drawImage(v, 0, 0, c.width, c.height);
            const dataUrl = c.toDataURL("image/jpeg", 0.5);
            const frame = dataUrl.split(",")[1];
            const r = await api.post(`/fel/sessions/${sessionId}/live-view/frame`, { frame });
            if (r.data?.stop) stopLiveView();
        } catch {
            // network or canvas hiccup — keep going
        } finally {
            pushFrameBusyRef.current = false;
        }
    };

    // Poll session every 4s to see if manager has requested live view
    useEffect(() => {
        if (!active || active.status !== "active") {
            stopLiveView();
            return;
        }
        const check = async () => {
            try {
                const r = await api.get(`/fel/sessions/${active.id}/live-view/frame`);
                const requested = !!r.data?.live_view_requested;
                if (requested && !liveViewActiveRef.current) await startLiveView(active.id);
                else if (!requested && liveViewActiveRef.current) stopLiveView();
            } catch { /* swallow */ }
        };
        check();
        liveCheckTimerRef.current = setInterval(check, 4000);
        return () => {
            if (liveCheckTimerRef.current) clearInterval(liveCheckTimerRef.current);
            stopLiveView();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active?.id, active?.status]);

    const sendPing = async (sid, lat, lng, speed_kmh, heading, accuracy_m) => {
        try {
            const r = await api.post(`/fel/sessions/${sid}/ping`, {
                latitude: lat, longitude: lng, speed_kmh, heading, accuracy_m,
            });
            const alerts = r.data?.proximity_alerts || [];
            const warns  = r.data?.proximity_warnings || [];
            const nb     = r.data?.nearby_operators || [];
            setProximity(alerts);
            setWarnings(warns);
            setNearby(nb);
            if (r.data?.zones) setZones(r.data.zones);
            if (alerts.length > 0)      handleProximityAlert(alerts, "alarm");
            else if (warns.length > 0)  handleProximityAlert(warns, "warn");
            // Update local session stats inline so HUD stays current
            setActive((a) => a ? {
                ...a,
                max_speed_kmh: r.data.max_speed_kmh, avg_speed_kmh: r.data.avg_speed_kmh,
                ping_count: r.data.ping_count, last_lat: lat, last_lng: lng,
            } : a);
        } catch (e) {
            // 401/403 — silently swallow
        }
    };

    const handleProximityAlert = (alerts, severity = "alarm") => {
        const now = Date.now();
        const throttle = severity === "alarm" ? 1800 : 5000;
        if (now - lastAlertAtRef.current < throttle) return;
        lastAlertAtRef.current = now;
        // Audible beep (Web Audio) — alarm = square wave 880→440 Hz, warn = softer sine 660 Hz
        try {
            if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
            const ctx = audioCtxRef.current;
            const o = ctx.createOscillator(); const g = ctx.createGain();
            if (severity === "alarm") {
                o.type = "square"; o.frequency.value = 880;
                g.gain.value = 0.25;
            } else {
                o.type = "sine"; o.frequency.value = 660;
                g.gain.value = 0.15;
            }
            o.connect(g); g.connect(ctx.destination);
            o.start();
            if (severity === "alarm") o.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.4);
            o.stop(ctx.currentTime + (severity === "alarm" ? 0.45 : 0.25));
        } catch { /* audio context may be suspended; user must tap to enable */ }
        if (navigator.vibrate) {
            navigator.vibrate(severity === "alarm" ? [300, 100, 300] : [150]);
        }
        const nearest = alerts[0];
        if (severity === "alarm") {
            toast.error(`⚠ ALARM · ${nearest.operator_name} (${nearest.machine}) — ${nearest.distance_m} m away`, { duration: 4000 });
        } else {
            toast.warning(`⚠ WARN · ${nearest.operator_name} (${nearest.machine}) — ${nearest.distance_m} m away`, { duration: 3000 });
        }
    };

    const startSession = async (payload) => {
        try {
            const r = await api.post("/fel/sessions/start", payload);
            setActive(r.data);
            setShowStart(false);
            toast.success("Operation started — keep the screen open");
            // Wake-lock if available — keeps the screen on in the cabin
            try {
                if ("wakeLock" in navigator) await navigator.wakeLock.request("screen");
            } catch { /* ignore */ }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not start session");
        }
    };

    const pause = async (reason, description) => {
        try {
            await api.post(`/fel/sessions/${active.id}/pause`, { reason, description });
            toast.success(`Paused — ${reason.replace("_", " ")}`);
            await loadActive();
            setShowPause(false);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to pause"); }
    };

    // Resume button behaviour:
    //   • If the current pause was for REFUELING → open the RefuelLogModal first.
    //     On submit we POST the fuel entry, then call /resume.
    //   • Otherwise resume immediately.
    const requestResume = () => {
        if (active?.pause_state?.reason === "refueling") {
            setShowRefuel(true);
        } else {
            resume();
        }
    };

    const resume = async () => {
        try {
            await api.post(`/fel/sessions/${active.id}/resume`);
            toast.success("Resumed");
            await loadActive();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to resume"); }
    };

    const logFuelAndResume = async (fuelData) => {
        try {
            if (fuelData && fuelData.litres > 0) {
                await api.post(`/fel/sessions/${active.id}/refuel`, fuelData);
            }
            await api.post(`/fel/sessions/${active.id}/resume`);
            setShowRefuel(false);
            if (fuelData && fuelData.litres > 0) {
                toast.success(`⛽ ${fuelData.litres} L logged · Resumed`);
            } else {
                toast.success("Resumed (no fuel logged)");
            }
            await loadActive();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to log fuel / resume");
        }
    };

    const stop = async (machine_hours_end, reason, notes, refuelData) => {
        try {
            await api.post(`/fel/sessions/${active.id}/stop`, { machine_hours_end, reason, notes });
            if (refuelData && refuelData.litres > 0) {
                try {
                    await api.post(`/fel/sessions/${active.id}/refuel`, refuelData);
                    toast.success(`Operation ended · ⛽ ${refuelData.litres} L logged`);
                } catch (e) {
                    toast.warning("Ended, but fuel entry failed: " + (e.response?.data?.detail || "unknown"));
                }
            } else {
                toast.success("Operation ended");
            }
            setActive(null);
            setShowStop(false);
            stopWatch();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to stop"); }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="fel-loading">Loading…</div>;

    return (
        <div className="min-h-screen bg-[var(--grindrod-navy)] text-white" data-testid="fel-operator-page">
            {/* Header */}
            <header className="px-4 py-3 flex items-center justify-between border-b border-white/10 bg-[var(--grindrod-navy)]">
                <div className="flex items-center gap-2">
                    <div className="bg-white p-1 rounded-sm"><GrindrodLogo size="xs" /></div>
                    <div>
                        <div className="font-display font-black uppercase text-sm tracking-wide">FEL Operator</div>
                        <div className="text-[10px] text-slate-300">{user?.name}</div>
                    </div>
                </div>
                <button onClick={logout} className="text-xs text-slate-300 inline-flex items-center gap-1.5" data-testid="fel-logout">
                    <SignOut size={14} /> Sign out
                </button>
            </header>

            {!active && (
                <div className="max-w-md mx-auto px-4 py-12 text-center">
                    <Truck size={64} weight="fill" className="mx-auto text-white/30 mb-4" />
                    <h1 className="font-display font-black text-3xl mb-2">Ready to start</h1>
                    <p className="text-slate-300 text-sm mb-8">
                        Tap the button below to log your machine and start the GPS-tracked operation.
                        Keep the screen open while you're working.
                    </p>
                    <button onClick={() => setShowStart(true)} className="w-full bg-[var(--grindrod-red)] hover:bg-red-700 text-white font-display font-black text-lg uppercase tracking-wider py-5 rounded-sm shadow-lg inline-flex items-center justify-center gap-2" data-testid="fel-start-btn">
                        <Play size={22} weight="fill" /> Start Operation
                    </button>
                </div>
            )}

            {active && active.status === "active" && (
                <ActiveSessionView session={active} gpsState={gpsState} currentSpeed={currentSpeed} lastPos={lastPos} proximity={proximity}
                                   warnings={warnings} nearby={nearby} zones={zones}
                                   liveViewActive={liveViewActive}
                                   onPause={() => setShowPause(true)} onResume={requestResume} onStop={() => setShowStop(true)} />
            )}

            {/* Hidden video + canvas used to push camera frames when a manager requests live view */}
            <video ref={liveVideoRef} autoPlay muted playsInline style={{ position: "absolute", left: -9999, top: -9999, width: 1, height: 1 }} />
            <canvas ref={liveCanvasRef} style={{ display: "none" }} />

            {/* Modals */}
            {showStart && <StartModal machines={machines} onStart={startSession} onClose={() => setShowStart(false)} />}
            {showPause && <PauseModal onPause={pause} onClose={() => setShowPause(false)} />}
            {showStop && <StopModal session={active} onStop={stop} onClose={() => setShowStop(false)} />}
            {showRefuel && (
                <RefuelLogModal
                    onSubmit={logFuelAndResume}
                    onSkip={() => logFuelAndResume(null)}
                    onClose={() => setShowRefuel(false)}
                />
            )}
        </div>
    );
}

/* ---------------- ACTIVE SESSION HUD ---------------- */
function ActiveSessionView({ session, gpsState, currentSpeed, lastPos, proximity, warnings, nearby, zones, liveViewActive, onPause, onResume, onStop }) {
    const [tick, setTick] = useState(0);
    useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
    const elapsedMin = useMemo(() => {
        try { return (Date.now() - new Date(session.started_at).getTime()) / 60000; } catch { return 0; }
    }, [session.started_at, tick]);
    const paused = !!session.pause_state;
    const isClose = proximity.length > 0;
    const nearest = proximity[0];

    return (
        <div className="px-3 py-3 max-w-lg mx-auto space-y-2">
            {/* Proximity overlay */}
            {isClose && (
                <div className="fixed inset-0 z-[80] bg-[var(--grindrod-red)]/70 backdrop-blur-sm flex items-center justify-center animate-pulse pointer-events-none" data-testid="fel-proximity-overlay">
                    <div className="text-center">
                        <Warning size={120} weight="fill" className="text-white mx-auto mb-3 drop-shadow-lg" />
                        <div className="font-display font-black text-5xl text-white drop-shadow-lg">SLOW DOWN</div>
                        <div className="font-display font-bold text-2xl text-white mt-2">{nearest?.operator_name} · {nearest?.distance_m} m</div>
                    </div>
                </div>
            )}

            {/* Status banner — compact */}
            <div className={`rounded-sm px-3 py-2 ${paused ? "bg-amber-500/20 border border-amber-400" : "bg-emerald-500/20 border border-emerald-400"}`}>
                <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${paused ? "bg-amber-400" : "bg-emerald-400 animate-pulse"}`} />
                    <div className="font-display font-black uppercase tracking-wide text-xs">
                        {paused ? `Paused · ${session.pause_state.reason.replace("_", " ")}` : "OPERATION ACTIVE"}
                    </div>
                    <div className="ml-auto text-[10px]">
                        GPS: <span className={gpsState === "live" ? "text-emerald-300" : "text-amber-300"}>{gpsState.toUpperCase()}</span>
                    </div>
                </div>
                <div className="text-[10px] text-slate-200 mt-0.5">
                    {session.machine_number} · {session.operation_type.replace("_", " ")} · started {new Date(session.started_at).toLocaleTimeString()}
                </div>
            </div>

            {/* Live-view notice — visible to the operator when management is streaming their camera */}
            {liveViewActive && (
                <div className="rounded-sm p-3 bg-[var(--grindrod-red)]/20 border border-red-400 flex items-center gap-2" data-testid="fel-live-view-banner">
                    <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                    <VideoCamera size={16} weight="fill" className="text-red-300" />
                    <div className="text-xs font-display font-bold uppercase tracking-wide">Management is viewing your camera</div>
                </div>
            )}

            {/* Speed gauge — compact */}
            <div className="bg-white/5 border border-white/10 rounded-sm px-4 py-2 flex items-center justify-between">
                <div>
                    <div className="uppercase tracking-wider text-[10px] text-slate-300">Current Speed</div>
                    <div className="font-display font-black text-4xl tabular leading-none">
                        {currentSpeed.toFixed(1)} <span className="text-xs text-slate-400 font-normal">km/h</span>
                    </div>
                </div>
                <div className="text-right text-[10px] text-slate-400">
                    <div>Max <b className="text-white tabular">{(session.max_speed_kmh || 0).toFixed(1)}</b></div>
                    <div>Avg <b className="text-white tabular">{(session.avg_speed_kmh || 0).toFixed(1)}</b></div>
                </div>
            </div>

            {/* Live proximity map — self + operators within 100 m */}
            <div className="rounded-sm border border-white/10 bg-white/5 p-2" data-testid="fel-mini-map-wrap">
                <div className="flex items-center gap-2 mb-1.5">
                    <MapPin size={13} weight="fill" className="text-sky-300" />
                    <div className="uppercase tracking-wider text-[10px] text-slate-300 font-bold">Live proximity ({zones?.visible_m ?? 100} m)</div>
                    <div className="ml-auto flex items-center gap-2 text-[10px]">
                        {(nearby || []).length > 0 && (
                            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400" /> {(nearby || []).length}</span>
                        )}
                        {(warnings || []).length > 0 && (
                            <span className="inline-flex items-center gap-1 text-amber-300 font-bold"><span className="w-2 h-2 rounded-full bg-amber-400" /> {warnings.length} WARN</span>
                        )}
                        {(proximity || []).length > 0 && (
                            <span className="inline-flex items-center gap-1 text-red-300 font-bold"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> {proximity.length} ALARM</span>
                        )}
                    </div>
                </div>
                <OperatorMiniMap
                    selfPos={lastPos ? { lat: lastPos.latitude ?? lastPos.lat, lng: lastPos.longitude ?? lastPos.lng } : null}
                    nearby={nearby}
                    machineNumber={session.machine_number}
                    zones={zones}
                    basemap="satellite"
                    height={140}
                />
            </div>

            {/* Distance to nearest machine — always visible, updated every poll */}
            {session.distance_to_nearest_m != null ? (
                <div className={`rounded-sm border p-3 flex items-center gap-3 ${
                    session.distance_to_nearest_m < 10
                        ? "bg-red-900/30 border-red-500/40"
                        : session.distance_to_nearest_m < 20
                            ? "bg-amber-900/30 border-amber-500/40"
                            : "bg-emerald-900/20 border-emerald-500/30"
                }`} data-testid="fel-nearest-machine">
                    <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center">
                        <ArrowsHorizontalIcon size={20} weight="fill" className={
                            session.distance_to_nearest_m < 10 ? "text-red-300"
                                : session.distance_to_nearest_m < 20 ? "text-amber-300" : "text-emerald-300"
                        } />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Nearest machine</div>
                        <div className="font-display font-black text-2xl text-white tabular">
                            {session.distance_to_nearest_m.toFixed(1)} m
                        </div>
                        <div className="text-[11px] text-slate-300 truncate">
                            {session.nearest_machine_id ? `${session.nearest_machine_id} approaching` : "no other machine in operation"}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="rounded-sm border border-slate-700 bg-slate-800/40 p-3 text-xs text-slate-400 text-center" data-testid="fel-nearest-machine-empty">
                    You are the only machine in operation
                </div>
            )}

            {/* Stats grid — compact */}
            <div className="grid grid-cols-4 gap-1.5">
                <StatTile Icon={Clock} label="Elapsed" value={`${Math.floor(elapsedMin / 60)}h ${Math.floor(elapsedMin % 60)}m`} testid="fel-elapsed" />
                <StatTile Icon={Pause} label="Delays" value={`${session.delays?.length || 0}`} testid="fel-delays" />
                <StatTile Icon={Warning} label="Prox" value={`${session.proximity_alerts || 0}`} testid="fel-prox-alerts" />
                <StatTile Icon={MapPin} label="Pings" value={`${session.ping_count || 0}`} testid="fel-pings" />
            </div>

            {/* Position card — 1 line */}
            <div className="bg-white/5 border border-white/10 rounded-sm px-3 py-1.5 text-[11px] text-slate-300 flex items-center gap-2">
                <MapPin size={12} className="text-emerald-400" />
                {lastPos ? `${lastPos.lat.toFixed(5)}, ${lastPos.lng.toFixed(5)}` : "Acquiring GPS…"}
                {gpsState === "denied" && <span className="ml-auto text-red-300">Location denied</span>}
            </div>

            {/* Action buttons — compact */}
            <div className="space-y-1.5 pt-1">
                {!paused ? (
                    <button onClick={onPause} className="w-full bg-amber-500 hover:bg-amber-600 text-[var(--grindrod-navy)] font-display font-black uppercase py-3 rounded-sm inline-flex items-center justify-center gap-2" data-testid="fel-pause-btn">
                        <Pause size={16} weight="fill" /> Pause Operation
                    </button>
                ) : (
                    <button onClick={onResume} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-display font-black uppercase py-3 rounded-sm inline-flex items-center justify-center gap-2" data-testid="fel-resume-btn">
                        <Play size={16} weight="fill" /> Resume
                    </button>
                )}
                <button onClick={onStop} className="w-full bg-[var(--grindrod-red)] hover:bg-red-700 text-white font-display font-black uppercase py-3 rounded-sm inline-flex items-center justify-center gap-2" data-testid="fel-stop-btn">
                    <Stop size={16} weight="fill" /> End Operation
                </button>
            </div>
        </div>
    );
}

function StatTile({ Icon, label, value, testid }) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-sm px-1.5 py-1.5 text-center" data-testid={testid}>
            <Icon size={12} className="mx-auto text-slate-400 mb-0.5" />
            <div className="text-[9px] uppercase tracking-wider text-slate-400 leading-none">{label}</div>
            <div className="font-display font-black text-sm tabular mt-0.5 leading-none">{value}</div>
        </div>
    );
}

/* ---------------- MODALS ---------------- */
function ModalShell({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
            <div className="bg-white text-slate-900 rounded-sm w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="bg-[var(--grindrod-navy)] text-white px-4 py-3 font-display font-black uppercase tracking-wide">{title}</div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}

function StartModal({ machines, onStart, onClose }) {
    const [f, setF] = useState({
        machine_id: "",
        machine_number: "",
        machine_hours_start: 0,
        operation_type: "vessel_load",
        division: "",
        vessel_id: "",
        lineup_id: "",
        notes: "",
    });
    const [options, setOptions] = useState({ items: [], scheduled_for_machine_count: 0 });
    const [loadingOpts, setLoadingOpts] = useState(false);
    const [picked, setPicked] = useState(null); // {kind:'vessel'|'lineup'|'other', id, vessel_name?, ...}

    // Refetch operations whenever the selected machine changes — items the
    // Fleet Manager has assigned to that machine are sorted to the top.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingOpts(true);
            try {
                const r = await api.get("/fel/operator/start-options",
                    f.machine_id ? { params: { machine_id: f.machine_id } } : undefined);
                if (cancelled) return;
                setOptions(r.data || { items: [] });
            } catch {
                if (!cancelled) setOptions({ items: [] });
            } finally {
                if (!cancelled) setLoadingOpts(false);
            }
        })();
        return () => { cancelled = true; };
    }, [f.machine_id]);

    const onMachineChange = (id) => {
        const m = machines.find((x) => x.id === id);
        setF((p) => ({
            ...p,
            machine_id: id,
            machine_number: m?.machine_number || "",
            machine_hours_start: m?.current_hours || 0,
        }));
        // Clear any vessel/lineup pick since allocations may differ for the new machine
        setPicked(null);
    };

    const pickOperation = (item) => {
        setPicked(item);
        if (item.kind === "vessel") {
            setF((p) => ({ ...p, operation_type: "vessel_load", vessel_id: item.id, lineup_id: "" }));
        } else if (item.kind === "lineup") {
            setF((p) => ({ ...p, operation_type: "vessel_load", vessel_id: "", lineup_id: item.id }));
        } else if (item.kind === "stockpile") {
            setF((p) => ({ ...p, operation_type: "stockpile", vessel_id: "", lineup_id: "" }));
        } else if (item.kind === "inbound") {
            setF((p) => ({ ...p, operation_type: "inbound", vessel_id: "", lineup_id: "" }));
        } else {
            setF((p) => ({ ...p, operation_type: "other", vessel_id: "", lineup_id: "" }));
        }
    };

    const submit = () => {
        if (!f.machine_id && !f.machine_number) return toast.error("Pick a machine from the fleet");
        if (!picked) return toast.error("Pick the operation you're working on");
        if (!f.division) return toast.error("Please select a division (GTRB or RBTG)");
        const payload = {
            machine_id: f.machine_id || null,
            machine_number: f.machine_number,
            machine_hours_start: parseFloat(f.machine_hours_start) || 0,
            operation_type: f.operation_type,
            division: f.division,
            vessel_id: f.operation_type === "vessel_load" ? (f.vessel_id || null) : null,
            lineup_id: f.operation_type === "vessel_load" ? (f.lineup_id || null) : null,
            notes: f.notes || null,
        };
        onStart(payload);
    };

    const fmtETB = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
    const otherOps = [
        { kind: "stockpile", title: "Stockpiling",  desc: "Move material to / from stockpile",   color: "#5A3A22" },
        { kind: "inbound",   title: "Inbound",      desc: "Inbound rail / truck unload",         color: "#B36B00" },
        { kind: "other",     title: "Other work",   desc: "Maintenance · cleaning · ad-hoc",     color: "#64748B" },
    ];

    return (
        <ModalShell title="Start FEL Operation" onClose={onClose}>
            <div className="space-y-3 text-sm">
                {/* Step 1 — Machine */}
                <div>
                    <label className="uppercase-label block mb-1">Step 1 · Pick your machine *</label>
                    {machines.length === 0 ? (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                            No in-service machines yet. Ask the Fleet Manager to add machines on the <b>Fleet</b> tab.
                        </div>
                    ) : (
                        <select className="g-input" value={f.machine_id} onChange={(e) => onMachineChange(e.target.value)} data-testid="fel-start-machine">
                            <option value="">— select your machine —</option>
                            {machines.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.machine_number}{m.make || m.model ? ` · ${[m.make, m.model].filter(Boolean).join(" ")}` : ""} · {(m.current_hours || 0).toFixed(1)} h{m.service_status === "service_warning" ? " · service due soon" : m.service_status === "service_overdue" ? " · SERVICE OVERDUE" : ""}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                <div>
                    <label className="uppercase-label block mb-1">Current machine hours *</label>
                    <input type="number" min="0" step="0.1" className="g-input tabular" value={f.machine_hours_start} onChange={(e) => setF({ ...f, machine_hours_start: e.target.value })} data-testid="fel-start-hours" />
                </div>

                {/* Division selector — required (iter-30) */}
                <div>
                    <label className="uppercase-label block mb-1">Division *</label>
                    <div className="grid grid-cols-2 gap-2" data-testid="fel-start-division">
                        {[
                            { key: "GTRB", title: "GTRB", sub: "Grindrod Terminals Richards Bay" },
                            { key: "RBTG", title: "RBTG", sub: "Richards Bay Terminals Grindrod" },
                        ].map((d) => (
                            <button
                                key={d.key}
                                type="button"
                                onClick={() => setF({ ...f, division: d.key })}
                                className={`text-left p-2 rounded-sm border-2 transition ${
                                    f.division === d.key
                                        ? "border-[var(--grindrod-red)] bg-red-50"
                                        : "border-slate-700 hover:border-slate-500 bg-slate-800/40"
                                }`}
                                data-testid={`fel-start-division-${d.key}`}
                            >
                                <div className={`font-display font-black text-base ${f.division === d.key ? "text-[var(--grindrod-red)]" : "text-white"}`}>{d.title}</div>
                                <div className={`text-[10px] ${f.division === d.key ? "text-slate-700" : "text-slate-400"}`}>{d.sub}</div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Step 2 — Operation picker (visual list of vessels + lineup + other) */}
                <div>
                    <label className="uppercase-label block mb-1">
                        Step 2 · Pick the operation you're working on *
                        {options.scheduled_for_machine_count > 0 && (
                            <span className="ml-2 g-pill g-pill-green normal-case tracking-normal" data-testid="fel-scheduled-pill">
                                {options.scheduled_for_machine_count} scheduled for this machine
                            </span>
                        )}
                    </label>
                    {loadingOpts ? (
                        <div className="text-xs text-slate-400 py-3 text-center">Loading operations…</div>
                    ) : (
                        <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="fel-operation-list">
                            {options.items.length === 0 && (
                                <div className="text-xs text-slate-400 italic px-2 py-1.5 border border-dashed border-slate-300 rounded-sm">
                                    No active vessels or scheduled line-up. Pick "Other work" below.
                                </div>
                            )}
                            {options.items.map((it) => {
                                const isPicked = picked && picked.kind === it.kind && picked.id === it.id;
                                return (
                                    <button
                                        key={`${it.kind}-${it.id}`}
                                        type="button"
                                        onClick={() => pickOperation(it)}
                                        data-testid={`fel-op-${it.kind}-${it.id}`}
                                        className={`w-full text-left px-2.5 py-2 rounded-sm border transition-colors ${
                                            isPicked
                                                ? "border-[var(--grindrod-red)] bg-red-50"
                                                : "border-slate-200 hover:border-[var(--grindrod-navy)] hover:bg-slate-50"
                                        }`}
                                    >
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`g-pill ${
                                                it.scheduled_for_machine ? "g-pill-green"
                                                : it.status === "active" ? "g-pill-red"
                                                : "g-pill-amber"
                                            } normal-case tracking-normal`}>
                                                {it.scheduled_for_machine ? "Scheduled" : it.status === "active" ? "Active" : "Line-up"}
                                            </span>
                                            <span className="font-display font-bold text-[var(--grindrod-navy)]">{it.vessel_name}</span>
                                            <span className="text-[11px] text-slate-500">· {it.client_name}</span>
                                        </div>
                                        <div className="text-[10.5px] text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
                                            {it.product && <span className="capitalize">{it.product}</span>}
                                            {it.tonnage && <span>· {Number(it.tonnage).toLocaleString()} t</span>}
                                            {it.current_hatch && <span>· Hatch H{it.current_hatch}</span>}
                                            {it.status === "active" && it.started_at && <span>· started {fmtETB(it.started_at)}</span>}
                                            {it.status === "scheduled" && it.etb && <span>· ETB {fmtETB(it.etb)}</span>}
                                        </div>
                                    </button>
                                );
                            })}
                            {/* Always-available "other" operations */}
                            <div className="pt-1 mt-1 border-t border-dashed border-slate-200">
                                <div className="uppercase-label text-[9px] text-slate-500 mb-1">Or non-vessel work</div>
                                {otherOps.map((o) => {
                                    const isPicked = picked && picked.kind === o.kind;
                                    return (
                                        <button key={o.kind} type="button" onClick={() => pickOperation(o)}
                                                data-testid={`fel-op-${o.kind}`}
                                                className={`w-full text-left px-2.5 py-1.5 rounded-sm border mb-1 transition-colors ${
                                                    isPicked ? "border-[var(--grindrod-red)] bg-red-50" : "border-slate-200 hover:border-[var(--grindrod-navy)] hover:bg-slate-50"
                                                }`}>
                                            <div className="flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: o.color }} />
                                                <span className="font-display font-bold text-[12px] text-[var(--grindrod-navy)]">{o.title}</span>
                                                <span className="text-[10px] text-slate-500">· {o.desc}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    <label className="uppercase-label block mb-1">Notes (optional)</label>
                    <input className="g-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
                </div>
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button onClick={submit} className="btn-primary" disabled={!f.machine_id || !picked} data-testid="fel-start-submit">Start operation</button>
                </div>
            </div>
        </ModalShell>
    );
}

function PauseModal({ onPause, onClose }) {
    const [reason, setReason] = useState("refueling");
    const [description, setDescription] = useState("");
    return (
        <ModalShell title="Pause Operation" onClose={onClose}>
            <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                    {["refueling", "shift_change", "breakdown", "operations_stop", "other"].map((r) => (
                        <button key={r} onClick={() => setReason(r)} className={`g-input !py-2 capitalize ${reason === r ? "!bg-[var(--grindrod-navy)] !text-white !border-[var(--grindrod-navy)]" : ""}`} data-testid={`fel-pause-reason-${r}`}>
                            {r.replace("_", " ")}
                        </button>
                    ))}
                </div>
                {reason === "refueling" && (
                    <div className="rounded-sm border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800" data-testid="fel-pause-refuel-hint">
                        ⛽ You&apos;ll be asked to enter litres pumped when you tap <b>Resume</b>.
                    </div>
                )}
                <div><label className="uppercase-label block mb-1">Description (optional)</label>
                    <input className="g-input" value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button onClick={() => onPause(reason, description)} className="btn-primary" data-testid="fel-pause-confirm">Pause</button>
                </div>
            </div>
        </ModalShell>
    );
}

function RefuelLogModal({ onSubmit, onSkip, onClose }) {
    const [litres, setLitres] = useState("");
    const [cost, setCost] = useState("");
    return (
        <ModalShell title="Log refuel · then Resume" onClose={onClose}>
            <div className="space-y-3 text-sm" data-testid="fel-refuel-modal">
                <div className="rounded-sm bg-amber-50 border border-amber-300 px-3 py-2 text-[11px] text-amber-800">
                    ⛽ Enter the litres you just pumped. It logs against this machine with the current date &amp; time and flows through all Fuel + Cost reports.
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                        <span className="text-[10px] text-slate-600">Litres pumped *</span>
                        <input type="number" min="0" step="0.1" className="g-input w-full text-lg tabular font-bold" value={litres} onChange={(e) => setLitres(e.target.value)} placeholder="e.g. 120" autoFocus data-testid="fel-refuel-litres" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] text-slate-600">Cost (R)</span>
                        <input type="number" min="0" step="0.01" className="g-input w-full text-lg tabular" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="optional" data-testid="fel-refuel-cost" />
                    </label>
                </div>
                <div className="flex gap-2 justify-between pt-2">
                    <button onClick={onSkip} className="btn-outline text-xs" data-testid="fel-refuel-skip">Skip &amp; Resume</button>
                    <button
                        onClick={() => onSubmit({ litres: parseFloat(litres) || 0, cost_zar: parseFloat(cost) || 0 })}
                        className="btn-primary"
                        disabled={!(parseFloat(litres) > 0)}
                        data-testid="fel-refuel-submit"
                    >Log &amp; Resume</button>
                </div>
            </div>
        </ModalShell>
    );
}

function StopModal({ session, onStop, onClose }) {
    const [hours, setHours] = useState(session?.machine_hours_start ? session.machine_hours_start + 1 : 0);
    const [reason, setReason] = useState("shift_end");
    const [notes, setNotes] = useState("");
    const [refuel, setRefuel] = useState(false);
    const [litres, setLitres] = useState("");
    const [cost, setCost] = useState("");
    return (
        <ModalShell title="End Operation" onClose={onClose}>
            <div className="space-y-3 text-sm">
                <div className="text-xs text-slate-500">Started at hours: <span className="tabular font-semibold">{session?.machine_hours_start || 0}</span></div>
                <div><label className="uppercase-label block mb-1">Closing machine hours *</label>
                    <input type="number" min={session?.machine_hours_start || 0} step="0.1" className="g-input tabular" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="fel-stop-hours" />
                </div>
                <div><label className="uppercase-label block mb-1">Reason</label>
                    <select className="g-input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="fel-stop-reason">
                        <option value="shift_end">Shift end</option>
                        <option value="breakdown">Breakdown</option>
                        <option value="operations_stop">Operations stop</option>
                        <option value="other">Other</option>
                    </select>
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={refuel} onChange={(e) => setRefuel(e.target.checked)} data-testid="fel-stop-refuel-toggle" />
                    <span>⛽ I refueled the machine — log fuel entry now</span>
                </label>
                {refuel && (
                    <div className="rounded-sm border border-amber-300 bg-amber-50 p-2 grid grid-cols-2 gap-2" data-testid="fel-stop-fuel-fields">
                        <label className="block">
                            <span className="text-[10px] text-slate-600">Litres *</span>
                            <input type="number" min="0" step="0.1" className="g-input w-full" value={litres} onChange={(e) => setLitres(e.target.value)} placeholder="e.g. 120" data-testid="fel-stop-litres" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] text-slate-600">Cost (R)</span>
                            <input type="number" min="0" step="0.01" className="g-input w-full" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="optional" data-testid="fel-stop-cost" />
                        </label>
                    </div>
                )}
                <div><label className="uppercase-label block mb-1">Notes</label>
                    <input className="g-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button
                        onClick={() => onStop(parseFloat(hours) || 0, reason, notes, refuel ? {
                            litres:        parseFloat(litres) || 0,
                            cost_zar:      parseFloat(cost) || 0,
                            hours_reading: parseFloat(hours) || null,
                        } : null)}
                        className="btn-primary"
                        data-testid="fel-stop-confirm"
                    >End</button>
                </div>
            </div>
        </ModalShell>
    );
}
